import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import { heroData } from '../data/mock';

const Hero = () => {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    // Trigger text reveal animation
    setTimeout(() => {
      document.querySelectorAll('.text-reveal-wrapper').forEach((wrapper, index) => {
        setTimeout(() => {
          wrapper.classList.add('reveal-active');
        }, index * 200);
      });
    }, 300);

    // Trigger scroll animations
    setTimeout(() => {
      document.querySelectorAll('.animate-on-scroll').forEach((el, index) => {
        setTimeout(() => {
          el.classList.add('active');
        }, 500 + index * 100);
      });
    }, 100);

    // Add glow effect to cards on mouse move
    const handleMouseMove = (e) => {
      const cards = document.querySelectorAll('.glow-card');
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        card.style.setProperty('--x', `${x}px`);
        card.style.setProperty('--y', `${y}px`);
      });
    };

    // Parallax — track scroll position to translate the headline upward
    let raf = 0;
    const handleScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setScrollY(window.scrollY || 0);
        raf = 0;
      });
    };

    document.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('scroll', handleScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section
      id="hero"
      className="relative flex items-center justify-center overflow-hidden border-b border-neutral-900 min-h-screen pt-24 pb-16"
    >
      {/* Soft top vignette to keep content legible over the canvas */}
      <div className="absolute inset-0 z-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)' }}></div>
      {/* Animated grid background */}
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-40"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,103,0,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,103,0,0.06) 1px, transparent 1px)',
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, #000 30%, transparent 75%)',
        }}
      ></div>

      {/* Orbiting glow lights */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute top-[15%] left-[15%] w-[520px] h-[520px] bg-gradient-to-tr from-[#FF6700]/30 via-[#FF6700]/10 to-transparent rounded-full blur-[120px] animate-float"></div>
        <div
          className="absolute bottom-[10%] right-[15%] w-[420px] h-[420px] bg-gradient-to-bl from-[#FF6700]/20 via-[#FF6700]/5 to-transparent rounded-full blur-[100px] animate-float"
          style={{ animationDelay: '-3s' }}
        ></div>
        <div
          className="absolute top-[55%] left-[55%] w-[280px] h-[280px] bg-[#FF6700]/15 rounded-full blur-[80px] animate-float"
          style={{ animationDelay: '-6s' }}
        ></div>
      </div>

      {/* Subtle scanlines */}
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-[0.04] mix-blend-overlay"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.5) 2px, rgba(255,255,255,0.5) 3px)',
        }}
      ></div>

      <div className="relative z-10 text-center px-4 max-w-7xl mx-auto">
        {/* Headline — parallax: moves upward faster than scroll, fades while leaving viewport */}
        <h1
          className="text-5xl md:text-7xl lg:text-8xl leading-[1.05] font-bold tracking-tight mb-3 text-[#EAEAEA] will-change-transform"
          style={{
            transform: `translateY(${scrollY * -0.35}px)`,
            opacity: Math.max(0, 1 - scrollY / 600),
          }}
        >
          Instituto Eneagrama
        </h1>

        <p
          className="text-xl md:text-2xl text-accent-orange font-light tracking-wide mb-12 will-change-transform"
          style={{
            transform: `translateY(${scrollY * -0.2}px)`,
            opacity: Math.max(0, 1 - scrollY / 700),
          }}
        >
          {t('hero.title_part1')}
        </p>

        <div className="flex flex-col md:flex-row items-center justify-center gap-6 animate-on-scroll delay-300">
          {/* Primary CTA — Orange */}
          <a href="#about" className="beam-btn group" data-testid="hero-cta-primary">
            <div className="beam-btn-content">
              <span>{t('hero.cta_primary')}</span>
              <iconify-icon
                icon="solar:arrow-right-linear"
                className="text-lg transition-transform group-hover:translate-x-1"
              ></iconify-icon>
            </div>
          </a>

          {/* Secondary CTAs */}
          <a
            href="/franqueado"
            data-testid="hero-cta-franchisee"
            className="text-xs font-mono uppercase tracking-widest text-neutral-400 hover:text-accent-orange transition-colors border-b border-transparent hover:border-accent-orange pb-1"
          >
            {t('footer.be_franchisee')}
          </a>

          <a
            href="/trabalhe-conosco"
            data-testid="hero-cta-join"
            className="text-xs font-mono uppercase tracking-widest text-neutral-400 hover:text-accent-orange transition-colors border-b border-transparent hover:border-accent-orange pb-1"
          >
            {t('footer.work_with_us')}
          </a>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16 animate-on-scroll delay-500">
          {heroData.stats.map((stat, index) => {
            const labelKeys = ['about.stats.years', 'about.stats.companies', 'about.stats.people', 'about.stats.units'];
            return (
              <div key={index} className="text-center">
                <div className="text-5xl md:text-6xl font-bold tracking-tighter mb-2 text-[#EAEAEA]">
                  {stat.value}
                </div>
                <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">
                  {t(labelKeys[index] || '')}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;